/**
 * RegionAnnotatorContext - Shared state management for RegionAnnotator
 *
 * This context provides shared state across all RegionAnnotator sub-components,
 * eliminating prop drilling and enabling better component composition.
 */

import { createContext, useContext } from 'react';
import type { RefObject } from 'react';
import type { Annotation } from './RegionAnnotator';
import type { PoseData } from '@/utils/landmarkDetection';
import type { Phase } from '@/types/api';
import type { HullData } from '@/utils/hullRenderer';

// =============================================================================
// Context State Interface
// =============================================================================

export interface RegionAnnotatorContextState {
  // Video refs
  videoRef: RefObject<HTMLVideoElement>;
  canvasRef: RefObject<HTMLCanvasElement>;

  // Playback state
  isPlaying: boolean;
  currentFrame: number;
  isVideoReady: boolean;
  fps: number;
  totalFrames: number;
  videoWidth: number;
  videoHeight: number;

  // Annotation state
  annotations: Annotation[];
  selectedAnnotationId: string | null;
  editingAnnotationId: string | null;
  onAnnotationsChange: (annotations: Annotation[]) => void;

  // Drawing state
  drawingMode: 'rectangle' | 'circle' | 'line' | null;
  isDrawing: boolean;
  setDrawingMode: (mode: 'rectangle' | 'circle' | 'line' | null) => void;
  setIsDrawing: (isDrawing: boolean) => void;

  // Pose/hull data
  poseData: PoseData | null;
  hulls: Record<string, HullData | null>;

  // Phase data
  currentPhase: Phase | null;
  phases: Phase[];

  // Video metadata
  videoId: string;
  videoUrl: string;

  // Template settings
  templateHandedness?: 'right' | 'left' | 'ambidextrous';
  facingDirection?: 'left' | 'right';
}

// =============================================================================
// Context Creation
// =============================================================================

// Exported so RegionAnnotatorProvider.tsx (react-refresh/only-export-components
// split — see that file's comment) can construct the Provider.
export const RegionAnnotatorContext = createContext<RegionAnnotatorContextState | null>(null);

// =============================================================================
// Consumer Hook
// =============================================================================

/**
 * Hook to access RegionAnnotator context
 *
 * @throws Error if used outside of RegionAnnotatorProvider
 * @returns RegionAnnotatorContextState
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { currentFrame, videoWidth, poseData } = useRegionAnnotator();
 *   // Use the context values...
 * }
 * ```
 */
export function useRegionAnnotator(): RegionAnnotatorContextState {
  const context = useContext(RegionAnnotatorContext);

  if (!context) {
    throw new Error(
      'useRegionAnnotator must be used within a RegionAnnotatorProvider. ' +
      'Ensure your component is wrapped with <RegionAnnotatorProvider>.'
    );
  }

  return context;
}

export type { HullData };
